import { ref, onMounted } from 'vue'
import type { Ref } from 'vue'

import { getDocumentPreviewParts } from './api'
import type { DocumentPreviewParts, PreviewEntry } from './types'
import type { PdftronDoc } from '../types'

interface UseDocumentPreviewParts {
  docs: Ref<PdftronDoc[]>
  loading: Ref<boolean>
  pending: Ref<boolean>
  failed: Ref<boolean>
}

// Resolves the PDFTron doc list for a set of documents. An entry with embedded
// `parts` needs no request; one with `parts: null` fetches them on mount.
// `pending` is on when at least one document has nothing to show yet, and
// `failed` when at least one fetch gave no answer. Both hold with a non-empty
// `docs` too, so a viewer can warn that the set is incomplete.
//
// PDFTron reads `initialDocs` once at mount, so `docs` is assigned once, after
// every fetch has settled, and the whole pane waits on the slowest fetch.
// Adding a fetched document to a mounted viewer needs a watcher in pdftron.vue
// and a MultiTab decision made on the entry count.
export function useDocumentPreviewParts(
  entries: PreviewEntry[]
): UseDocumentPreviewParts {
  const needsFetch = entries.some((entry) => entry.parts === null)
  const docs = ref<PdftronDoc[]>([])
  const loading = ref(needsFetch)
  const pending = ref(false)
  const failed = ref(false)

  function apply(resolved: (DocumentPreviewParts | null | undefined)[]): void {
    docs.value = entries.flatMap((entry, index) => {
      const parts = resolved[index]
      if (!parts) return []
      return parts.parts.map((part) => ({
        url: part.url,
        filename: entry.filename,
        extension: entry.extension,
      }))
    })
    pending.value = resolved.some((parts) => parts?.pending === true)
    failed.value = resolved.some((parts) => !parts)
    loading.value = false
  }

  async function load(): Promise<void> {
    apply(
      await Promise.all(
        entries.map((entry) =>
          entry.parts === null
            ? getDocumentPreviewParts(entry.id)
            : Promise.resolve(entry.parts)
        )
      )
    )
  }

  if (needsFetch) {
    onMounted(load)
  } else {
    apply(entries.map((entry) => entry.parts))
  }

  return { docs, loading, pending, failed }
}
